import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

const DRAWER_TRANSITION_MS = 220;

interface AppDrawerProps {
  open: boolean;
  onRequestClose: () => void;
  children: ReactNode;
  titleId?: string;
  aiIdPrefix?: string;
}

export function AppDrawer({
  open,
  onRequestClose,
  children,
  titleId,
  aiIdPrefix = 'app-drawer',
}: AppDrawerProps) {
  const [shouldRender, setShouldRender] = useState(open);
  const [isVisible, setIsVisible] = useState(false);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onRequestClose();
      }
    },
    [onRequestClose, open],
  );

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      const animationFrame = window.requestAnimationFrame(() => setIsVisible(true));
      return () => window.cancelAnimationFrame(animationFrame);
    }

    setIsVisible(false);
    const timeoutId = window.setTimeout(() => setShouldRender(false), DRAWER_TRANSITION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [open]);

  useEffect(() => {
    if (!shouldRender) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, shouldRender]);

  if (!shouldRender) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-ai-id={aiIdPrefix}
    >
      <button
        type="button"
        aria-label="닫기"
        className={`absolute inset-0 h-full w-full bg-slate-950/35 transition-opacity duration-200 ease-out focus:outline-none focus-visible:ring-4 focus-visible:ring-white/60 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onRequestClose}
        data-ai-id={`${aiIdPrefix}-backdrop-button`}
      />

      <aside
        className={`absolute inset-y-0 right-0 flex h-full w-[92vw] max-w-[640px] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-200 ease-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-ai-id={`${aiIdPrefix}-panel`}
      >
        <div className="flex h-full min-h-0 flex-col" data-ai-id={`${aiIdPrefix}-panel-content`}>
          {children}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
